import { useEffect, useState } from 'react';
import type { InventoryItemDto } from '@bb/shared';
import { api, newKey, type ProgressNews } from '../net/api';
import type { AvatarLook } from '../art/avatar';
import { Sheet } from './Sheet';
import { Thumb } from './Thumb';
import { AvatarPreview } from './AvatarPreview';

const TABS = [
  { key: 'clothing', label: 'Clothes', match: (i: InventoryItemDto) => !!i.slot },
  { key: 'furniture', label: 'Furniture', match: (i: InventoryItemDto) => i.placeable },
  { key: 'fish', label: 'Fish', match: (i: InventoryItemDto) => i.type === 'fish' },
] as const;

export function Backpack({ look, equipped, onClose, onChanged }: { look: AvatarLook; equipped: Record<string, string>; onClose: () => void; onChanged: (news?: ProgressNews) => void }) {
  const [items, setItems] = useState<InventoryItemDto[]>([]);
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('clothing');
  const [wearing, setWearing] = useState(equipped);
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string }>();

  const load = () => api.inventory().then((r) => setItems(r.items), (e: Error) => setMessage({ kind: 'err', text: e.message }));
  useEffect(() => {
    void load();
  }, []);

  async function toggle(i: InventoryItemDto) {
    const slot = i.slot!;
    const next = wearing[slot] === i.key ? null : i.key;
    try {
      const r = await api.equip({ [slot]: next });
      setWearing(r.equipped);
      onChanged();
    } catch (e) {
      setMessage({ kind: 'err', text: (e as Error).message });
    }
  }

  async function sell(i: InventoryItemDto, quantity: number) {
    try {
      const r = await api.sell(i.key, quantity, newKey());
      setMessage({ kind: 'ok', text: `Sold ${quantity} × ${i.name}. You now have ${r.balance.toLocaleString()} coins.` });
      onChanged((r as { progress?: ProgressNews }).progress);
      await load();
    } catch (e) {
      setMessage({ kind: 'err', text: (e as Error).message });
    }
  }

  const current = TABS.find((t) => t.key === tab)!;
  const shown = items.filter(current.match);
  const lookNow: AvatarLook = { ...look, wear: Object.fromEntries(Object.entries(wearing).map(([s, k]) => [s, items.find((i) => i.key === k)?.render ?? look.wear?.[s] ?? {}])) };

  return (
    <Sheet title="Backpack" subtitle="Everything you own. Clothes go on with one click." onClose={onClose} wide>
      <div className="backpack">
        <div className="backpack-preview"><AvatarPreview look={lookNow} scale={2} /></div>
        <div className="stack">
          <div className="tabs" role="tablist">
            {TABS.map((t) => (
              <button key={t.key} role="tab" aria-selected={tab === t.key} className={tab === t.key ? 'tab on' : 'tab'} onClick={() => setTab(t.key)}>
                {t.label} ({items.filter(t.match).reduce((n, i) => n + i.quantity, 0)})
              </button>
            ))}
          </div>
          {message && <p className={message.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{message.text}</p>}
          {shown.length === 0 && (
            <p className="muted">{tab === 'fish' ? 'No fish yet. Try the fishing spot at the end of the pier on Pebble Beach.' : tab === 'clothing' ? 'No clothes yet. Tidewear Outfitters in Town Square has plenty.' : 'No furniture.'}</p>
          )}
          <div className="item-grid">
            {shown.map((i) => (
              <article key={i.key} className={`item-card rarity-${i.rarity}`}>
                <Thumb item={i} look={look} size={72} />
                <h3>{i.name}{i.quantity > 1 && <span className="qty"> × {i.quantity}</span>}</h3>
                <div className="item-foot">
                  {i.slot && (
                    <button className={wearing[i.slot] === i.key ? 'btn small' : 'btn small ghost'} onClick={() => void toggle(i)}>
                      {wearing[i.slot] === i.key ? 'Wearing' : 'Wear'}
                    </button>
                  )}
                  {i.placeable && <span className="muted small-print">Place it at home</span>}
                  {i.sellable && i.sellPrice > 0 && (
                    <button className="btn small ghost" onClick={() => void sell(i, i.type === 'fish' ? i.quantity : 1)}>
                      Sell{i.type === 'fish' && i.quantity > 1 ? ' all' : ''} · {i.sellPrice * (i.type === 'fish' ? i.quantity : 1)}c
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </Sheet>
  );
}
